<script setup lang="ts">
  import { ThemeSwitcher, Tooltip, IconImage } from '@/shared/ui';

  export interface BranchThemeColors {
    primaryColor: string;
    secondaryColor: string;
    textColor: string;
    primaryColorDark: string;
    secondaryColorDark: string;
    textColorDark: string;
  }
  import ImageDropzone from './ImageDropzone.vue';
  import Eye from '@/shared/assets/icons/EyeGray.svg';
  import Trash from '@/shared/assets/icons/TrashIcon.svg';
  import FileImage from '@/shared/assets/icons/FileImage.svg';

  interface Props {
    primaryColor: string;
    secondaryColor: string;
    textColor: string;
    primaryColorDark?: string;
    secondaryColorDark?: string;
    textColorDark?: string;
    logoName?: string;
    logoPath?: string;
    allUploadsComplete: boolean;
  }

  interface Emits {
    (e: 'files-selected', files: File[]): void;
    (e: 'upload-progress', fileId: string, progress: number): void;
    (e: 'upload-complete', fileId: string, imageIds?: number[]): void;
    (e: 'view-logo'): void;
    (e: 'delete-logo'): void;
    (e: 'all-files-deleted'): void;
    (e: 'colors-changed', colors: BranchThemeColors): void;
  }

  defineProps<Props>();
  defineEmits<Emits>();
</script>

<template>
  <div class="w-full divide-y divide-gray-02">
    <div class="p-6">
      <div class="h-auto flex flex-col gap-2">
        <div class="font-semibold flex">
          Logo
        </div>
        <div class="mt-3 text-sm text-gray-03 text-left">
          A logo deve estar nos formatos SVG ou PNG. Se o background for claro, escolha uma logo de
          cor preta ou alguma tonalidade escura; em caso de background escuro, escolha uma logo
          branca ou clara.
        </div>
        <ImageDropzone
          v-if="!logoPath"
          :filiais="true"
          :all-uploads-complete="allUploadsComplete"
          @files-selected="(files) => $emit('files-selected', files)"
          @upload-progress="(fileId, progress) => $emit('upload-progress', fileId, progress)"
          @upload-complete="(fileId, imageIds) => $emit('upload-complete', fileId, imageIds)"
          @all-files-deleted="$emit('all-files-deleted')"
        />
        <div
          v-else
          class="flex justify-between items-center w-full p-4 bg-[rgba(227,227,227,0.20)] border-gray-02 border rounded-xl"
        >
          <div class="flex flex-row items-center gap-2">
            <IconImage
              :src="FileImage"
              alt="Ícone de Imagem"
              class="text-gray-07"
            />
            <span class="text-md text-gray-07 text-left">{{ logoName }}</span>
          </div>
          <div class="flex gap-3">
            <Tooltip content="Visualizar">
              <button class="flex items-center justify-center">
                <IconImage
                  :src="Eye"
                  alt="Ícone de Visualização"
                  class="text-gray-05 mt-1"
                  @click="$emit('view-logo')"
                />
              </button>
            </Tooltip>
            <Tooltip content="Deletar">
              <button class="flex items-center justify-center">
                <IconImage
                  :src="Trash"
                  alt="Ícone de Deleção"
                  class="text-gray-05"
                  @click="$emit('delete-logo')"
                />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>

    <div class="p-6">
      <div class="h-auto flex flex-col gap-2">
        <div class="font-semibold flex">
          Cores
        </div>
        <div class="w-[70%] pt-2 pb-2">
          <ThemeSwitcher
            :initial-primary-color="primaryColor"
            :initial-secondary-color="secondaryColor"
            :initial-text-color="textColor"
            :initial-primary-color-dark="primaryColorDark"
            :initial-secondary-color-dark="secondaryColorDark"
            :initial-text-color-dark="textColorDark"
            @colors-changed="$emit('colors-changed', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
